'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { ArrowLeft, CalendarPlus, Copy, Download, Pencil, Trash2 } from 'lucide-react';
import { Badge, Button, EmptyState, Loader } from '@/shared/components/ui';
import ConfirmModal from '@/shared/components/ui/common/ConfirmModal';
import { useCalendarioDetalhe, type FiltroAbrangencia } from '../hooks/useCalendarioDetalhe';
import { ABRANGENCIA_LABEL, type Feriado, type FeriadoInput } from '../types/feriadosTypes';
import { CopiarFeriadosDialog } from './CopiarFeriadosDialog';
import { FeriadoFormDialog } from './FeriadoFormDialog';
import { ImportarNacionaisDialog } from './ImportarNacionaisDialog';
const FILTROS: Array<{
  valor: FiltroAbrangencia;
  rotulo: string;
}> = [{
  valor: 'todas',
  rotulo: 'Todas'
}, {
  valor: 'NACIONAL',
  rotulo: 'Nacional'
}, {
  valor: 'ESTADUAL',
  rotulo: 'Estadual'
}, {
  valor: 'MUNICIPAL',
  rotulo: 'Municipal'
}];
function formatarDataBR(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  if (!ano || !mes || !dia) return iso;
  return `${dia}/${mes}/${ano}`;
}
function abrangenciaDetalhe(feriado: Feriado): string {
  if (feriado.abrangencia === 'ESTADUAL') return feriado.uf ?? '';
  if (feriado.abrangencia === 'MUNICIPAL') {
    return [feriado.municipio, feriado.uf].filter(Boolean).join('/');
  }
  return '';
}
interface CalendarioDetalheContainerProps {
  ano: number;
}
export function CalendarioDetalheContainer(t0) {
  const $ = _c(79);
  const {
    ano
  } = t0;
  const {
    calendario,
    status,
    salvando,
    filtroAbrangencia,
    setFiltroAbrangencia,
    feriadosFiltrados,
    anosDisponiveis,
    adicionarFeriado,
    editarFeriado,
    removerFeriado,
    importarNacionais,
    copiarDeAno,
    voltar
  } = useCalendarioDetalhe(ano);
  const [formAberto, setFormAberto] = useState(false);
  const [feriadoEdicao, setFeriadoEdicao] = useState(null);
  const [copiarAberto, setCopiarAberto] = useState(false);
  const [importarAberto, setImportarAberto] = useState(false);
  const [feriadoRemover, setFeriadoRemover] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setFeriadoEdicao(null);
      setFormAberto(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const abrirNovo = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = feriado => {
      setFeriadoEdicao(feriado);
      setFormAberto(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const abrirEdicao = t2;
  let t3;
  if ($[2] !== adicionarFeriado || $[3] !== editarFeriado) {
    t3 = (input, id) => id ? editarFeriado(id, input) : adicionarFeriado(input);
    $[2] = adicionarFeriado;
    $[3] = editarFeriado;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSalvarFeriado = t3;
  let t4;
  if ($[5] !== feriadoRemover || $[6] !== removerFeriado) {
    t4 = async () => {
      if (!feriadoRemover) {
        return;
      }
      const ok = await removerFeriado(feriadoRemover.id);
      if (ok) {
        setFeriadoRemover(null);
      }
    };
    $[5] = feriadoRemover;
    $[6] = removerFeriado;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const confirmarRemocao = t4;
  if (status === "loading") {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex h-64 w-full items-center justify-center"><Loader size="large" /></div>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  if (status === "succeeded" && !calendario) {
    let t5;
    if ($[9] !== ano) {
      t5 = <span className="text-body font-body text-subtext-color">Não encontramos um calendário para {ano}.</span>;
      $[9] = ano;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== voltar) {
      t6 = <Button variant="neutral-secondary" onClick={voltar}>Voltar para a lista</Button>;
      $[11] = voltar;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t5 || $[14] !== t6) {
      t7 = <div className="flex w-full flex-col items-center gap-3 p-9">{t5}{t6}</div>;
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    return t7;
  }
  let t5;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowLeft className="size-4" />;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== voltar) {
    t6 = <Button variant="neutral-tertiary" size="small" icon={t5} onClick={voltar} aria-label="Voltar" />;
    $[17] = voltar;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== ano) {
    t7 = <h1 className="text-heading-2 font-heading-2 text-default-font">Calendário {ano}</h1>;
    $[19] = ano;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = calendario?.feriados.length ?? 0;
  const t9 = (calendario?.feriados.length ?? 0) === 1 ? "" : "s";
  const t10 = (calendario?.feriados.length ?? 0) === 1 ? "" : "s";
  let t11;
  if ($[21] !== t10 || $[22] !== t8 || $[23] !== t9) {
    t11 = <span className="text-caption font-caption text-subtext-color">{t8} feriado{t9} cadastrado{t10}</span>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t7) {
    t12 = <div className="flex flex-col">{t7}{t11}</div>;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t6) {
    t13 = <div className="flex items-center gap-3">{t6}{t12}</div>;
    $[28] = t12;
    $[29] = t6;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Button variant="neutral-secondary" size="small" icon={<Download className="size-3.5" />} onClick={() => setImportarAberto(true)}>Importar nacionais</Button>;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Button variant="neutral-secondary" size="small" icon={<Copy className="size-3.5" />} onClick={() => setCopiarAberto(true)}>Copiar de outro ano</Button>;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex flex-wrap items-center gap-2">{t14}{t15}<Button variant="brand-primary" size="small" icon={<CalendarPlus className="size-3.5" />} onClick={abrirNovo}>Adicionar feriado</Button></div>;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t13) {
    t17 = <div className="flex flex-wrap items-center justify-between gap-4">{t13}{t16}</div>;
    $[34] = t13;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== filtroAbrangencia || $[37] !== setFiltroAbrangencia) {
    t18 = FILTROS.map(filtro => <Button key={filtro.valor} variant={filtroAbrangencia === filtro.valor ? "brand-secondary" : "neutral-tertiary"} size="small" onClick={() => setFiltroAbrangencia(filtro.valor)}>{filtro.rotulo}</Button>);
    $[36] = filtroAbrangencia;
    $[37] = setFiltroAbrangencia;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18) {
    t19 = <div className="flex flex-wrap items-center gap-2">{t18}</div>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== feriadosFiltrados || $[42] !== filtroAbrangencia) {
    t20 = <div className="bg-default-background flex w-full flex-col gap-2 rounded-[16px] border border-solid border-neutral-200 p-5">{feriadosFiltrados.length === 0 ? <EmptyState title="Nenhum feriado" description={filtroAbrangencia === "todas" ? "Importe os nacionais ou adicione feriados estaduais." : "Nenhum feriado nessa abrang\xEAncia."} /> : <div className="flex flex-col">{feriadosFiltrados.map(feriado_0 => <div key={feriado_0.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-solid border-neutral-100 py-3 last:border-b-0"><div className="flex min-w-0 items-center gap-3"><span className="text-body-bold font-body-bold text-default-font tabular-nums">{formatarDataBR(feriado_0.data)}</span><div className="flex min-w-0 flex-col"><span className="text-body font-body text-default-font truncate">{feriado_0.nome}</span>{abrangenciaDetalhe(feriado_0) ? <span className="text-caption font-caption text-subtext-color">{abrangenciaDetalhe(feriado_0)}</span> : null}</div></div><div className="flex items-center gap-2"><Badge variant="neutral">{ABRANGENCIA_LABEL[feriado_0.abrangencia]}</Badge>{feriado_0.origem === "NACIONAL" ? <span className="text-caption font-caption text-subtext-color">Importado</span> : <><Button variant="neutral-tertiary" size="small" icon={<Pencil className="size-3.5" />} onClick={() => abrirEdicao(feriado_0)} aria-label="Editar feriado" /><Button variant="neutral-tertiary" size="small" icon={<Trash2 className="size-3.5" />} onClick={() => setFeriadoRemover(feriado_0)} aria-label="Remover feriado" /></>}</div></div>)}</div>}</div>;
    $[41] = feriadosFiltrados;
    $[42] = filtroAbrangencia;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== ano || $[45] !== feriadoEdicao || $[46] !== formAberto || $[47] !== handleSalvarFeriado || $[48] !== salvando) {
    t21 = <FeriadoFormDialog open={formAberto} onOpenChange={setFormAberto} salvando={salvando} ano={ano} feriadoEdicao={feriadoEdicao} onSalvar={handleSalvarFeriado} />;
    $[44] = ano;
    $[45] = feriadoEdicao;
    $[46] = formAberto;
    $[47] = handleSalvarFeriado;
    $[48] = salvando;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== ano || $[51] !== anosDisponiveis || $[52] !== copiarAberto || $[53] !== copiarDeAno || $[54] !== salvando) {
    t22 = <CopiarFeriadosDialog open={copiarAberto} onOpenChange={setCopiarAberto} salvando={salvando} anoDestino={ano} anosDisponiveis={anosDisponiveis} onCopiar={copiarDeAno} />;
    $[50] = ano;
    $[51] = anosDisponiveis;
    $[52] = copiarAberto;
    $[53] = copiarDeAno;
    $[54] = salvando;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== ano || $[57] !== importarAberto || $[58] !== importarNacionais || $[59] !== salvando) {
    t23 = <ImportarNacionaisDialog open={importarAberto} onOpenChange={setImportarAberto} ano={ano} salvando={salvando} onImportar={importarNacionais} />;
    $[56] = ano;
    $[57] = importarAberto;
    $[58] = importarNacionais;
    $[59] = salvando;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const t24 = Boolean(feriadoRemover);
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setFeriadoRemover(null);
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== confirmarRemocao) {
    t26 = () => void confirmarRemocao();
    $[62] = confirmarRemocao;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== feriadoRemover) {
    t27 = feriadoRemover ? `Remover "${feriadoRemover.nome}" (${formatarDataBR(feriadoRemover.data)})? Essa ação não pode ser desfeita.` : "";
    $[64] = feriadoRemover;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== salvando || $[67] !== t24 || $[68] !== t26 || $[69] !== t27) {
    t28 = <ConfirmModal isOpen={t24} onClose={t25} onConfirm={t26} title="Remover feriado" message={t27} variant="danger" confirmText="Remover" isLoading={salvando} />;
    $[66] = salvando;
    $[67] = t24;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t17 || $[72] !== t19 || $[73] !== t20 || $[74] !== t21 || $[75] !== t22 || $[76] !== t23 || $[77] !== t28) {
    t29 = <div className="flex w-full flex-col gap-6 p-9">{t17}{t19}{t20}{t21}{t22}{t23}{t28}</div>;
    $[71] = t17;
    $[72] = t19;
    $[73] = t20;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
    $[77] = t28;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  return t29;
}